import React, { useEffect, useState } from "react";
import "../../styles/Profiles.css";
import ChangePassword from "../User/ChangePassword";
import NotificationPreferences from "../Shared/NotificationPreferences";
import { API_BASE_URL } from "../../config";

// The editable fields here were first name, last name, street, city, state,
// zip and phone. None of them were used for anything — the server never sent
// them to Stripe and never used them for billing or tax — so they have been
// removed from the model. What is left is what the account is: a name, the
// email you sign in with, and the cards Stripe holds for you.

export default function CustomerProfile() {
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({ display_name: "", email: "" });
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const token = localStorage.getItem("token");
    const auth = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/customer/profile`, { headers: auth })
            .then(async (res) => {
                if (!res.ok) throw new Error();
                const data = await res.json();
                setProfile(data);
                setForm({ display_name: data.display_name || "", email: data.email || "" });
            })
            .catch(() => setError("Failed to load your profile."))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setError("");
        setNotice("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/profile`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", ...auth },
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || "Could not save those changes.");
                setSaving(false);
                return;
            }
            setProfile(data);
            setEditing(false);
            setNotice(
                data.email_verified
                    ? "Saved."
                    : "Saved. Confirm your new email address using the link we just sent."
            );
        } catch {
            setError("Network error. Nothing was saved.");
        }
        setSaving(false);
    }

    async function resendVerification() {
        setNotice("");
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
                method: "POST",
                headers: auth,
            });
            const data = await res.json().catch(() => ({}));
            setNotice(data.message || "Confirmation email sent.");
        } catch {
            setError("Could not send the confirmation email.");
        }
    }

    if (loading) return <div className="wb-page">Loading your profile…</div>;
    if (!profile) return <div className="wb-page wb-note wb-note--danger">{error}</div>;

    return (
        <div className="wb-page wb-page--narrow">
            <div className="wb-page-head">
                <h1>Your account</h1>
            </div>

            {notice && <div className="wb-note wb-note--ok">{notice}</div>}
            {error && <div className="wb-note wb-note--danger">{error}</div>}

            <section className="wb-card" style={{ marginTop: "1rem" }}>
                {editing ? (
                    <form onSubmit={handleSave}>
                        <label className="wb-field">
                            <span className="wb-label">Name</span>
                            <input
                                className="wb-input" value={form.display_name}
                                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                                required
                            />
                        </label>
                        <label className="wb-field">
                            <span className="wb-label">Email</span>
                            <input
                                className="wb-input" type="email" value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                            />
                            <span className="wb-hint">
                                Changing this signs you in with the new address, and it will
                                need confirming again.
                            </span>
                        </label>
                        <div className="wb-row">
                            <button type="submit" className="wb-btn wb-btn--primary" disabled={saving}>
                                {saving ? "Saving…" : "Save"}
                            </button>
                            <button
                                type="button" className="wb-btn wb-btn--secondary"
                                onClick={() => {
                                    setEditing(false);
                                    setForm({ display_name: profile.display_name, email: profile.email });
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                ) : (
                    <>
                        <div className="wb-field">
                            <span className="wb-label">Name</span>
                            <div>{profile.display_name}</div>
                        </div>
                        <div className="wb-field">
                            <span className="wb-label">Email</span>
                            <div className="wb-row">
                                <span>{profile.email}</span>
                                {profile.email_verified ? (
                                    <span className="wb-badge wb-badge--ok">Confirmed</span>
                                ) : (
                                    <span className="wb-badge wb-badge--warn">Unconfirmed</span>
                                )}
                            </div>
                            {!profile.email_verified && (
                                <button
                                    type="button" className="wb-btn wb-btn--ghost wb-btn--sm"
                                    onClick={resendVerification}
                                    style={{ marginTop: 4, paddingLeft: 0 }}
                                >
                                    Send the confirmation email again
                                </button>
                            )}
                        </div>
                        <button className="wb-btn wb-btn--secondary" onClick={() => setEditing(true)}>
                            Edit
                        </button>
                    </>
                )}
            </section>

            <section style={{ marginTop: "2rem" }}>
                <h2>Password</h2>
                <div className="wb-card">
                    <ChangePassword />
                </div>
            </section>

            <section style={{ marginTop: "2rem" }}>
                <h2>Notifications</h2>
                <div className="wb-card">
                    <NotificationPreferences />
                </div>
            </section>

            {/* Payment methods moved to their own dashboard tab, mirroring the
                broadcaster's "Payout & balance". They were at the bottom of
                this page while the broadcaster's payout settings had a tab of
                their own -- the same information at two different depths on
                the two sides of the same product. */}
        </div>
    );
}
